// Typed confirm (36E §1.2) — for an action that is irreversible or reaches the whole fleet.
// The primary stays disabled until the operator types the EXACT phrase, and the phrase is
// an identifier of what is about to happen (an operation key, the last four characters of a
// licence key, "REVOKE 12"), never "yes" — so a confirm cannot be satisfied by habit.
//
// Rides the same DS modal shell as ReasonDialog (ui/dialog.tsx): scrim, focus trap, Esc.
// While `busy` nothing dismisses it and nothing can be submitted twice.
import React, { useEffect, useId, useState } from 'react';
import { Icon } from '@/components/shared/Icon';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';

/** The "Type X to confirm" field, shared with ReasonDialog's `confirmPhrase`. */
export function PhraseField({
  phrase, value, onChange, disabled, onEnter,
}: {
  phrase: string; value: string; onChange: (v: string) => void; disabled?: boolean; onEnter?: () => void;
}) {
  const id = useId();
  const matches = value === phrase;
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 6 }}>
        Type <code style={{ fontFamily: 'var(--mono, monospace)', fontWeight: 700, color: 'var(--text-primary)' }}>{phrase}</code> to confirm
      </label>
      <input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && matches && onEnter) { e.preventDefault(); onEnter(); } }}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${matches ? 'var(--status-expired)' : 'var(--border)'}`, fontSize: 13, fontFamily: 'var(--mono, monospace)', color: 'var(--text-primary)', background: 'var(--surface)', boxSizing: 'border-box' }}
      />
    </div>
  );
}

export interface TypedConfirmDialogProps {
  open: boolean;
  title: string;
  body: string;
  /** The exact text the operator must type. */
  phrase: string;
  confirmLabel: string;
  danger?: boolean;
  /** The confirmed action is in flight: dismissal and resubmission are blocked. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function TypedConfirmDialog({ open, title, body, phrase, confirmLabel, danger = true, busy = false, onConfirm, onCancel }: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState('');
  // Never carry a typed phrase over to the next time the dialog opens: a confirm that is
  // already satisfied on reopen is no confirm at all (DF-15).
  useEffect(() => { if (!open) setTyped(''); }, [open]);

  const canConfirm = !busy && typed === phrase;
  const submit = () => { if (canConfirm) onConfirm(); };
  const accent = danger ? 'var(--status-expired)' : 'var(--accent)';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onCancel(); }}>
      <DialogContent
        onEscapeKeyDown={(e) => { if (busy) e.preventDefault(); }}
        onInteractOutside={(e) => { if (busy) e.preventDefault(); }}
        style={{ maxWidth: 520 }}
      >
        <div style={{ display: 'flex', gap: 12 }}>
          <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 9, background: danger ? 'var(--ps-pill-red-bg)' : 'var(--accent-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name="alert-triangle" size={18} color={accent} />
          </span>
          <div style={{ flex: 1 }}>
            <DialogTitle style={{ fontSize: 15.5, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</DialogTitle>
            <DialogDescription style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>{body}</DialogDescription>
          </div>
        </div>
        <PhraseField phrase={phrase} value={typed} onChange={setTyped} disabled={busy} onEnter={submit} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={() => !busy && onCancel()} disabled={busy} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface-raised)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          <button type="button" onClick={submit} disabled={!canConfirm} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: accent, color: '#fff', fontSize: 13, fontWeight: 600, cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontFamily: 'inherit' }}>{busy ? 'Working…' : confirmLabel}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TypedConfirmDialog;
